import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress, TextField } from '@mui/material';

import { addOfficial } from 'store/actions/contactDetails';
import { fetchSearchedOfficials } from 'store/actions/officials';
import { officialsSelectors } from 'store/selectors/officials';

import { SearchResultOption, UserDialog } from 'common/components';

const StyledTextField = styled(TextField)`
  width: 100%;
  padding-bottom: 15px;
`;

const FieldsWrapper = styled.div`
  margin-top: 20px;
`;

const officialMeta = official => official.office || official.position;

const officialName = official => `${official.firstName} ${official.lastName}`;

const OfficialsForm = ({
  contactId,
  handleDialogClose,
  isDialogOpen,
  listId,
  organizationUuid,
}) => {
  const dispatch = useDispatch();
  const { isSearching, searchedOfficials } = useSelector(state => ({
    isSearching: officialsSelectors.isFetching(state),
    searchedOfficials: officialsSelectors.getSearchedOfficials(state),
  }));

  const [description, setDescription] = useState('');
  const [searchedPhrase, setSearchedPhrase] = useState('');
  const [selectedOfficial, setSelectedOfficial] = useState(null);

  const debouncedFetchSearchedOfficials = useCallback(
    debounce(phrase => {
      dispatch(fetchSearchedOfficials(phrase, organizationUuid));
    }, 500),
    [organizationUuid]
  );

  const handleDialogUpdate = () =>
    dispatch(
      addOfficial(listId, contactId, {
        description,
        official: selectedOfficial.uuid,
      })
    );

  const handleOfficialsDialogClose = () => {
    setDescription('');
    setSearchedPhrase('');
    setSelectedOfficial(null);
    handleDialogClose();
  };

  const handleOfficialChange = (_, newValue) => {
    setSelectedOfficial(newValue);

    if (!newValue) {
      setDescription('');
    }
  };

  return (
    <UserDialog
      description={description}
      handleDialogClose={handleOfficialsDialogClose}
      handleDataUpdate={handleDialogUpdate}
      isDialogOpen={isDialogOpen}
      isOfficialsDialog
      title="Add a grasstops relationship"
      subtitle="Find the related official:"
    >
      <FieldsWrapper>
        <Autocomplete
          fullWidth
          // https://github.com/mui/material-ui/issues/20068
          filterOptions={x => x}
          getOptionLabel={option => `${officialName(option)}, ${officialMeta(option)}`}
          inputValue={searchedPhrase}
          loading={isSearching}
          onChange={handleOfficialChange}
          onInputChange={(_, newValue) => {
            if (newValue.length > 2) {
              debouncedFetchSearchedOfficials(newValue);
            }

            setSearchedPhrase(newValue);
          }}
          options={searchedOfficials.filter(official => official.category !== 'custom')}
          renderOption={(props, option) => (
            <SearchResultOption
              key={option.uuid}
              meta={officialMeta(option)}
              name={officialName(option)}
              optionProps={props}
            />
          )}
          renderInput={params => (
            <StyledTextField
              {...params}
              label="Search officials"
              variant="outlined"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          value={selectedOfficial}
        />
        {!!selectedOfficial && (
          <StyledTextField
            label="Description"
            multiline
            onChange={event => setDescription(event.target.value)}
            placeholder="How do they know each other?"
            value={description}
            variant="standard"
          />
        )}
      </FieldsWrapper>
    </UserDialog>
  );
};

OfficialsForm.defaultProps = {
  contactId: '',
  listId: '',
};

OfficialsForm.propTypes = {
  contactId: PropTypes.string,
  handleDialogClose: PropTypes.func.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  listId: PropTypes.string,
  organizationUuid: PropTypes.string.isRequired,
};

export default OfficialsForm;
